import { useState } from 'react'
import { X, Receipt, CheckSquare, Square } from 'lucide-react'
import despesasService from '../../services/despesasService'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Modal único de gerenciamento de despesas dos lotes: adicionar uma despesa
 * nova, ou remover/restaurar (override pós-faturamento, sem apagar o
 * lançamento) o que já existe — duas abas de uma mesma ferramenta, em vez de
 * dois botões/modais separados fazendo a mesma coisa em telas diferentes.
 *
 * Props:
 * - lotes: array de lotes com { id, numero_lote, vv, desp, despesas_removidas_manualmente }
 * - organizadorId: int
 * - onClose: () => void
 * - onSaved: () => void (callback após salvar para recarregar dados)
 */
export default function DespesasModal({ lotes, organizadorId, onClose, onSaved }) {
  const [tab, setTab] = useState('adicionar') // 'adicionar' | 'gerenciar'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-purple-600" />
            <h2 className="text-base font-semibold text-gray-800">Despesas dos Lotes</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3">
          <button
            onClick={() => setTab('adicionar')}
            className={`px-3 py-1.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              tab === 'adicionar' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Adicionar despesa
          </button>
          <button
            onClick={() => setTab('gerenciar')}
            className={`px-3 py-1.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              tab === 'gerenciar' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Remover / restaurar
          </button>
        </div>
        <div className="border-b border-gray-100" />

        {tab === 'adicionar'
          ? <AbaAdicionar lotes={lotes} organizadorId={organizadorId} onClose={onClose} onSaved={onSaved} />
          : <AbaGerenciar lotes={lotes} organizadorId={organizadorId} onClose={onClose} onSaved={onSaved} />}
      </div>
    </div>
  )
}

function AbaAdicionar({ lotes, organizadorId, onClose, onSaved }) {
  const [titulo, setTitulo] = useState('')
  const [valor, setValor] = useState('')
  const [modo, setModo] = useState('primeiro') // 'primeiro' | 'todos'
  const [saving, setSaving] = useState(false)

  const lotesAlvo = modo === 'primeiro' ? [lotes[0]] : lotes
  const valorNum = parseFloat(valor) || 0
  const totalImpacto = valorNum * lotesAlvo.length

  const handleSalvar = async () => {
    if (!titulo.trim()) {
      toast.error('Informe a descrição da despesa')
      return
    }
    if (valorNum <= 0) {
      toast.error('Informe um valor válido')
      return
    }

    setSaving(true)
    try {
      await despesasService.adicionarSeparadaBatch(organizadorId, {
        lote_ids: lotesAlvo.map(l => l.id),
        titulo: titulo.trim(),
        valor: valorNum,
      })
      toast.success(`Despesa adicionada em ${lotesAlvo.length} lote${lotesAlvo.length > 1 ? 's' : ''}`)
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar despesa')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
          <input
            type="text"
            value={titulo}
            onChange={e => setTitulo(e.target.value)}
            placeholder="Ex: Frete, Vacina, Curral..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor (R$)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={valor}
            onChange={e => setValor(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSalvar() }}
            placeholder="0,00"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Aplicar em</label>
          <div className="space-y-2">
            <div
              onClick={() => setModo('primeiro')}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                modo === 'primeiro' ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-purple-200'
              }`}
            >
              {modo === 'primeiro' ? <CheckSquare className="w-4 h-4 text-purple-600" /> : <Square className="w-4 h-4 text-gray-400" />}
              <div>
                <p className={`text-sm font-medium ${modo === 'primeiro' ? 'text-purple-900' : 'text-gray-700'}`}>
                  Somente no primeiro lote
                </p>
                <p className="text-xs text-gray-500">Lote {lotes[0]?.numero_lote} — {fmt(lotes[0]?.vv)}</p>
              </div>
            </div>
            <div
              onClick={() => setModo('todos')}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                modo === 'todos' ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-purple-200'
              }`}
            >
              {modo === 'todos' ? <CheckSquare className="w-4 h-4 text-purple-600" /> : <Square className="w-4 h-4 text-gray-400" />}
              <div>
                <p className={`text-sm font-medium ${modo === 'todos' ? 'text-purple-900' : 'text-gray-700'}`}>
                  Em todos os lotes ({lotes.length})
                </p>
                <p className="text-xs text-gray-500">Uma despesa de {fmt(valorNum)} em cada lote</p>
              </div>
            </div>
          </div>
        </div>

        {valorNum > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Total de despesas a inserir:</span>
              <span className="font-bold text-gray-900">{fmt(totalImpacto)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {lotesAlvo.length} lote{lotesAlvo.length > 1 ? 's' : ''} × {fmt(valorNum)}
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
        <button onClick={onClose}
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
          Cancelar
        </button>
        <button onClick={handleSalvar} disabled={saving || !titulo.trim() || valorNum <= 0}
          className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
          {saving ? 'Salvando...' : `Adicionar em ${lotesAlvo.length} lote${lotesAlvo.length > 1 ? 's' : ''}`}
        </button>
      </div>
    </>
  )
}

function AbaGerenciar({ lotes, organizadorId, onClose, onSaved }) {
  const [saving, setSaving] = useState(null) // null | 'remover' | 'restaurar'
  const [selectedIds, setSelectedIds] = useState(() => new Set(lotes.map(l => l.id)))

  const todosSelected = selectedIds.size === lotes.length
  const nenhumSelected = selectedIds.size === 0

  const toggleLote = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleTodos = () => {
    setSelectedIds(todosSelected ? new Set() : new Set(lotes.map(l => l.id)))
  }

  const handleAplicar = async (removida) => {
    if (nenhumSelected) {
      toast.error('Selecione pelo menos um lote')
      return
    }
    setSaving(removida ? 'remover' : 'restaurar')
    try {
      await lotesService.removerDespesasBatch(organizadorId, {
        lote_ids: Array.from(selectedIds),
        removida,
      })
      toast.success(
        removida
          ? `Despesas removidas em ${selectedIds.size} lote${selectedIds.size > 1 ? 's' : ''}`
          : `Despesas restauradas em ${selectedIds.size} lote${selectedIds.size > 1 ? 's' : ''}`
      )
      onSaved()
      onClose()
    } catch (err) {
      toast.error(removida ? 'Erro ao remover despesas' : 'Erro ao restaurar despesas')
      console.error(err)
    } finally {
      setSaving(null)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-purple-600">{selectedIds.size}</span> de {lotes.length} lote{lotes.length > 1 ? 's' : ''} selecionado{selectedIds.size !== 1 ? 's' : ''}
          </p>
          <button
            onClick={toggleTodos}
            className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors"
          >
            {todosSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {todosSelected ? 'Desmarcar todos' : 'Selecionar todos'}
          </button>
        </div>

        <div className="space-y-0.5 mb-4 max-h-56 overflow-y-auto border border-gray-100 rounded-lg p-2">
          {lotes.map(l => {
            const isSelected = selectedIds.has(l.id)
            const removida = !!l.despesas_removidas_manualmente
            return (
              <div
                key={l.id}
                onClick={() => toggleLote(l.id)}
                className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors text-sm ${
                  isSelected ? 'hover:bg-purple-50 bg-white' : 'hover:bg-gray-50 bg-gray-50/50 opacity-60'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleLote(l.id)}
                  onClick={e => e.stopPropagation()}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
                />
                <span className={`font-medium ${isSelected ? 'text-gray-800' : 'text-gray-400'}`}>
                  Lote {l.numero_lote}
                </span>
                <div className="flex items-center gap-2 text-gray-500 ml-auto">
                  <span className={`${!isSelected ? 'text-gray-300' : removida ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {fmt(l.desp)}
                  </span>
                  {removida && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                      isSelected ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      Removida
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-800">
            Remove o valor das despesas cobradas nos lotes selecionados sem apagar
            os lançamentos — a qualquer momento é possível restaurar.
          </p>
        </div>
      </div>

      <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
        <button onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          Cancelar
        </button>
        <button onClick={() => handleAplicar(false)} disabled={!!saving || nenhumSelected}
          className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {saving === 'restaurar' ? 'Restaurando...' : 'Restaurar selecionados'}
        </button>
        <button onClick={() => handleAplicar(true)} disabled={!!saving || nenhumSelected}
          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          {saving === 'remover' ? 'Removendo...' : 'Remover selecionados'}
        </button>
      </div>
    </>
  )
}
